import { useState } from 'react'
import { Coffee, MoreHorizontal, Trash2, Copy } from 'lucide-react'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'
import { OptimizedImage } from '@/shared/ui'
import type { DrinkEntry } from '@/features/drink-history/api/use-drink-history'

// Маппинг mood → emoji
const MOOD_EMOJI: Record<string, string> = {
  relaxed: '😌',
  focused: '🎯',
  energized: '⚡',
  happy: '😊',
}

export interface DiaryEntryCardProps {
  entry: DrinkEntry
  onEdit?: (entryId: string) => void
  onDelete?: (entryId: string) => void
  onDuplicate?: (entryId: string) => void
}

/**
 * Компактная карточка записи дневника
 * 
 * Клик по карточке → редактирование
 * Клик по меню (3 точки) → дополнительные действия
 */
export function DiaryEntryCard({
  entry,
  onEdit,
  onDelete,
  onDuplicate,
}: DiaryEntryCardProps) {
  const { hapticFeedback } = useTelegram()
  const [showActions, setShowActions] = useState(false)

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000)
    
    if (diffDays === 0) return 'Сегодня'
    if (diffDays === 1) return 'Вчера'
    if (diffDays < 7) return `${diffDays} дн. назад`
    
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    })
  }
  
  // Клик по карточке → редактирование
  const handleCardClick = () => {
    hapticFeedback.impactOccurred('light')
    onEdit?.(entry.id)
  }
  
  // Emoji настроения
  const moodEmoji = entry.mood ? MOOD_EMOJI[entry.mood] : null

  return (
    <div className="bg-white dark:bg-dark-card rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden animate-in fade-in duration-200">
      {/* Основной контент — кликабельный для редактирования */}
      <button
        type="button"
        onClick={handleCardClick}
        className="w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-dark-elevated/50 transition-colors active:scale-[0.99]"
      >
        <div className="flex items-start gap-3">
          {/* Фото чая или иконка */}
          {(entry.userPhotoUrl || entry.photoUrl) ? (
            <OptimizedImage
              src={entry.userPhotoUrl || entry.photoUrl || ''}
              alt={entry.teaName || 'Чай'}
              preset="thumb"
              wrapperClassName="w-12 h-12 rounded-xl flex-shrink-0 bg-gray-100 dark:bg-dark-elevated"
              className="object-cover"
            />
          ) : null}
          <div className={cn(
            'w-12 h-12 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0',
            (entry.userPhotoUrl || entry.photoUrl) && 'hidden'
          )}>
            <Coffee className="w-5 h-5 text-primary dark:text-primary-light" />
          </div>

          {/* Информация */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-ink dark:text-white truncate">
                  {entry.teaName || 'Чай'}
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(entry.drankAt)}
                </span>
              </div>
              
              {/* Рейтинг + Настроение */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Emoji настроения */}
                {moodEmoji && (
                  <span className="text-base" title={entry.mood}>
                    {moodEmoji}
                  </span>
                )}
              
              {/* Рейтинг */}
              {entry.rating && (
                  <div className="flex items-center gap-0.5 text-amber-500">
                  <span className="text-sm font-medium">{entry.rating}</span>
                  <span className="text-sm">★</span>
                </div>
              )}
            </div>
            </div>

            {/* Параметры заваривания */}
            {entry.brewingParams?.temperature && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Заваривание: {entry.brewingParams.temperature}°C
                {entry.brewingParams.time && ` · ${entry.brewingParams.time}с`}
              </p>
            )}

            {/* Заметка */}
            {entry.notes && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 line-clamp-2">
                {entry.notes}
              </p>
            )}
          </div>
        </div>
        </button>

      {/* Панель действий */}
      <div className="flex items-center justify-end px-4 py-2 bg-gray-50 dark:bg-dark-elevated/50 border-t border-gray-100 dark:border-dark-border">
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              hapticFeedback.impactOccurred('light')
              setShowActions(!showActions)
            }}
            className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-card rounded-lg transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          {/* Dropdown меню */}
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowActions(false)} 
              />
              <div className="absolute right-0 bottom-full mb-1 w-40 bg-white dark:bg-dark-card rounded-xl shadow-lg border border-gray-100 dark:border-dark-border z-20 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                <button
                  type="button"
                  onClick={() => {
                    hapticFeedback.impactOccurred('light')
                    setShowActions(false)
                    onDuplicate?.(entry.id)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  <span>Повторить</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    hapticFeedback.impactOccurred('medium')
                    setShowActions(false)
                    onDelete?.(entry.id)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Удалить</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
